import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthenticationProvider } from 'nikao-authentication';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import Login from './components/auth/Login';
import Layout from './components/Layout';
import Dashboard from './pages/Dashboard';

import SOWManagement from './pages/SOWManagement';
import ForecastRevenue from './pages/ForecastRevenue';
import AvailableDays from './pages/AvailableDays';
import ActualsManagement from './pages/ActualsManagement';
import ActualsGroupedView from './pages/ActualsGroupedView';
import Clients from './pages/Clients';
import Resources from './pages/Resources';
import ResourceTypes from './pages/ResourceTypes';
import UserManagement from './pages/UserManagement';
import { nikaoColors } from './theme/nikaoTheme';

const msalClientId = process.env.REACT_APP_MSAL_CLIENT_ID || '';
const msalTenantId = process.env.REACT_APP_MSAL_TENANT_ID || 'common';
const msalAuthority = `https://login.microsoftonline.com/${msalTenantId}`;

// Get redirect URI from environment variable or use current origin
// window.location.origin automatically works for both localhost and production
const getRedirectUri = () => {
  if (typeof window === 'undefined') return undefined;
  
  // Use explicit redirect URI from environment if set (useful for Docker/containers)
  if (process.env.REACT_APP_MSAL_REDIRECT_URI) {
    return process.env.REACT_APP_MSAL_REDIRECT_URI;
  }
  
  // Default to current origin (works for localhost:3001, production, etc.)
  return window.location.origin;
};

function App() {
  const redirectUri = getRedirectUri();
  
  // Log the redirect URI being used for debugging
  console.log('[MSAL Config]', {
    clientId: msalClientId,
    tenantId: msalTenantId,
    authority: msalAuthority,
    redirectUri: redirectUri,
    apiUrl: process.env.REACT_APP_API_URL
  });
  
  return (
    <AuthenticationProvider
      applicationId={msalClientId}
      authority={msalAuthority}
      redirectUri={redirectUri}
    >
      <AuthProvider>
        <Router>
          <div className="min-h-screen bg-gray-50">
            <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/sow" element={
              <ProtectedRoute>
                <Layout>
                  <SOWManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/forecast-revenue" element={
              <ProtectedRoute>
                <Layout>
                  <ForecastRevenue />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/available-days" element={
              <ProtectedRoute>
                <Layout>
                  <AvailableDays />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/actuals" element={
              <ProtectedRoute>
                <Layout>
                  <ActualsManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/actuals-grouped" element={
              <ProtectedRoute>
                <Layout>
                  <ActualsGroupedView />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/clients" element={
              <ProtectedRoute>
                <Layout>
                  <Clients />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/resources" element={
              <ProtectedRoute>
                <Layout>
                  <Resources />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/resource-types" element={
              <ProtectedRoute>
                <Layout>
                  <ResourceTypes />
                </Layout>
              </ProtectedRoute>
            } />

            <Route path="/users" element={
              <ProtectedRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <Toaster 
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: nikaoColors.surface,
                color: nikaoColors.text,
                border: `1px solid ${nikaoColors.border}`,
                borderRadius: '0',
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: nikaoColors.success,
                  secondary: '#fff',
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: nikaoColors.error,
                  secondary: '#fff',
                },
              },
            }}
            />
          </div>
        </Router>
      </AuthProvider>
    </AuthenticationProvider>
  );
}

export default App; 